import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  ClipboardList, 
  AlertTriangle, 
  BookOpen,
  Settings,
  LogOut,
  Zap,
  Menu,
  Archive,
  MessageCircle,
  Download,
  ClipboardCheck,
  Calendar,
  Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';

// NavLink component defined outside Layout
const NavLinkItem = ({ item, isActive, mobile, onMobileClick }) => {
  const Icon = item.icon;
  
  return (
    <Link
      to={item.path}
      onClick={() => mobile && onMobileClick()}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
        isActive 
          ? 'bg-[#0056D2] text-white' 
          : item.highlight 
            ? 'text-amber-300 hover:bg-amber-900/30 hover:text-amber-200 border border-amber-500/30'
            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
      }`}
      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{item.label}</span>
      {item.highlight && !isActive && (
        <Badge className="bg-amber-600 text-white text-xs ml-auto">Review</Badge>
      )}
    </Link>
  );
};

// SidebarContent component defined outside Layout
const SidebarContent = ({ user, filteredNavItems, location, handleLogout, mobile, onMobileClick }) => (
  <>
    {/* Logo */}
    <div className="p-6 border-b border-slate-700">
      <Link to="/dashboard" className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#0056D2] rounded-lg flex items-center justify-center">
          <Zap className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="font-black text-white text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
            PROJECT
          </h1>
          <p className="text-xs text-slate-400 -mt-1">Command Center</p>
        </div>
      </Link>
    </div>

    {/* Navigation */}
    <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
      {filteredNavItems.map((item) => {
        const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
        return (
          <NavLinkItem 
            key={item.path} 
            item={item} 
            isActive={isActive}
            mobile={mobile}
            onMobileClick={onMobileClick}
          />
        );
      })}
    </nav>

    {/* User Profile */}
    <div className="p-4 border-t border-slate-700">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#0056D2] flex items-center justify-center text-white font-bold">
          {user?.full_name?.charAt(0) || 'U'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
          <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
        </div>
      </div>
      <Button 
        variant="ghost" 
        className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
        onClick={handleLogout}
      >
        <LogOut className="w-4 h-4 mr-2" />
        Sign Out
      </Button>
    </div>
  </>
);

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isSupervisor } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/work-orders', label: 'Work Orders', icon: ClipboardList },
    { path: '/schedule', label: 'Schedule', icon: Calendar, supervisorOnly: true },
    { path: '/crew-requests', label: 'Crew Requests', icon: Users, supervisorOnly: true },
    { path: '/supervisor-review', label: 'Supervisor Review', icon: ClipboardCheck, supervisorOnly: true, highlight: true },
    { path: '/workflow', label: 'Workflow Center', icon: Zap, supervisorOnly: true },
    { path: '/chat', label: 'Team Chat', icon: MessageCircle },
    { path: '/downloads', label: 'Downloads', icon: Download },
    { path: '/archive', label: 'Archive', icon: Archive },
    { path: '/bulletins', label: 'Bulletins', icon: AlertTriangle },
    { path: '/lessons', label: 'Lessons Learned', icon: BookOpen },
  ];

  // Filter items based on role
  const filteredNavItems = navItems.filter(item => {
    if (item.supervisorOnly && !isSupervisor() && !isAdmin()) return false;
    return true;
  });

  if (isAdmin()) {
    filteredNavItems.push({ path: '/admin', label: 'Admin', icon: Settings });
  }

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-[#0F172A]">
        <SidebarContent 
          user={user}
          filteredNavItems={filteredNavItems}
          location={location}
          handleLogout={handleLogout}
          mobile={false}
          onMobileClick={closeMobileMenu}
        />
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#0F172A] border-b border-slate-700">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#0056D2] rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
              PROJECT
            </span>
          </Link>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-[#0F172A] border-slate-700">
              <SidebarContent 
                user={user}
                filteredNavItems={filteredNavItems}
                location={location}
                handleLogout={handleLogout}
                mobile={true}
                onMobileClick={closeMobileMenu}
              />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className="md:pl-64 pt-16 md:pt-0">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
